import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { PlusIcon, PaperAirplaneIcon, PaperClipIcon, LinkIcon } from '@heroicons/react/24/outline';

import { Badge, Button, Card, CardHeader, ContentLoader, Input } from '@/components/common';
import { useInsurersLookup } from '@/hooks/useInsurers';
import { quoteService, getErrorMessage } from '@/services';
import { formatCurrency, formatDate, titleCase } from '@/utils';
import { coverLabel } from '@/utils/coverTypes';

import {
  buildOptionPatch,
  optionEditPolicy,
  useAddQuoteOption,
  useFetchPremium,
  useIntegratedInsurers,
  useQuoteOptions,
  useSetQuoteVehicleVariant,
  useUpdateQuoteOption,
  vehicleCodeMissingFrom,
  type IntegratedInsurer,
  type OptionEditForm,
  type QuoteOption,
  type VehicleCodeMissing,
} from './proposalFlow';
import {
  emptyPremiumOption,
  expiryDayFromTimestamp,
  formatExpiryDay,
  localDateKey,
  premiumFigureErrors,
  premiumOptionErrors,
  premiumOptionToFormData,
  type PremiumOptionDraft,
} from './wizard/premiumOptions';
import { LifeIllustrationTable } from './LifeIllustrationTable';
import { PremiumOptionRow, type VehicleCategory } from './wizard/steps/common/PremiumOptionsStep';

/**
 * TASK 14 -- the premium options on the broker's own quote screen.
 *
 * THE BACKEND FOR THIS ALREADY EXISTED. Board #245 part 3 merged the two kinds
 * of option: `apps.integrations.tasks._persist_quote` (an insurer's API) and
 * `QuoteOptionCreateSerializer` (a broker typing a figure in) both write
 * `Quote(status="received")` rows onto the SAME `QuoteRequest`, and one
 * serializer renders both with a derived `pricing_source` saying which is
 * which (backend/apps/quotes/serializers.py:258). The compare screen showed
 * that merge; the broker's own quote screen showed nothing at all -- no list,
 * no way to add an option after the wizard had run, no way to adjust one before
 * the quote went to the customer.
 *
 * In live data every one of the 37 options is hand-entered: none has an
 * `api_response` or an `insurer_quote_id`. So the manual column below is the
 * ordinary case and the API column is the rare one, not the other way round.
 *
 * WHY EDITING IS ASYMMETRIC -- see `optionEditPolicy` in ./proposalFlow, which
 * owns that rule and explains it. Short version: a manual figure has only one
 * copy and it is ours; an API-priced figure has a second copy held by the
 * insurer that our PATCH cannot reach.
 */
interface QuotePremiumOptionsCardProps {
  requestId: string;
  /** Scopes the Product dropdown on the add form, exactly as the wizard does. */
  insuranceTypeId?: string;
  /**
   * B14 -- this quote's vehicle type (`two_wheeler` | `private_car` |
   * `commercial_vehicle`), read by the caller off
   * `quote.additional_data.vehicle_data.vehicle_type` for a motor quote and
   * `undefined` for every other line. Narrows the add-option form's Product
   * dropdown to that vehicle type, exactly as the wizard's own
   * `PremiumOptionsStep` does -- see `resolveProductDropdownState` there.
   */
  vehicleCategory?: VehicleCategory;
  /** `QuoteRequest.is_shared` -- the flag that decides whether the customer's
   *  public link resolves at all. Reaching the `quote_shared` status is its
   *  side effect, not the other way round (see views._publish). */
  isShared: boolean;
  sharedAt: string | null;
  status: string;
  /** Whether THIS viewer owns the quote. The quote screen is reachable by
   *  POSPs and insurer logins as well as the broker's own staff, and only the
   *  broker's staff publish the customer's link -- the same rule the rest of
   *  QuoteDetailPage already applies to the status controls. False hides the
   *  share panel outright rather than showing a button that is not theirs. */
  canShare: boolean;
  /** Who `share_via` would actually reach. Absent means that channel would be
   *  refused by the server, so the button says so instead of failing. */
  proposerPhone?: string | null;
  proposerEmail?: string | null;
}

/**
 * Statuses that end the story. `lifecycle.TERMINAL_STATUSES` is the same set.
 *
 * The card goes read-only on these because `share` is idempotent and flips
 * `is_shared` REGARDLESS of status -- only the move onto the `quote_shared`
 * rung is best-effort (`QuoteRequestViewSet._publish`). So "Send to Customer"
 * on a cancelled quote would quietly bring its public link back to life, and
 * editing a completed one would rewrite the figure the customer already bought
 * at. The endpoints still accept both; this is the invitation being withdrawn,
 * not a permission.
 */
const CLOSED_STATUSES = new Set(['completed', 'cancelled', 'expired']);

export function QuotePremiumOptionsCard({
  requestId,
  insuranceTypeId,
  vehicleCategory,
  isShared,
  sharedAt,
  status,
  canShare,
  proposerPhone,
  proposerEmail,
}: QuotePremiumOptionsCardProps) {
  const queryClient = useQueryClient();
  const { data: options, isLoading, isError, error } = useQuoteOptions(requestId);
  const addOption = useAddQuoteOption(requestId);
  // Same lookup the wizard's step feeds its rows from, so the two forms offer
  // the same list of insurance companies.
  const { data: insurers, isLoading: insurersLoading } = useInsurersLookup();
  const insurerOptions = useMemo(
    () => (insurers ?? []).map((insurer) => ({ value: insurer.id, label: insurer.name })),
    [insurers]
  );
  const updateOption = useUpdateQuoteOption(requestId);

  // BOARD #278. Which insurers on this broker could actually be asked for a
  // live premium -- configuration only, no insurer call, so it is safe to ask
  // on every render. The button appears ONLY for those: offering it where the
  // integration is not switched on is a button that fails when pressed.
  const { data: integrated } = useIntegratedInsurers(requestId);
  const fetchPremium = useFetchPremium(requestId);
  const [fetchingInsurerId, setFetchingInsurerId] = useState<string | null>(null);

  // B11. A missing vehicle code used to end at a toast. The 502 now carries the
  // catalogued vehicles it could mean; the broker picks one, the quote is
  // repointed at it, and the fetch runs again.
  const setVehicleVariant = useSetQuoteVehicleVariant(requestId);
  const [codeGap, setCodeGap] = useState<{
    insurer: IntegratedInsurer;
    gap: VehicleCodeMissing;
  } | null>(null);
  const [chosenVariantId, setChosenVariantId] = useState('');

  function fetchFrom(insurer: IntegratedInsurer) {
    setFetchingInsurerId(insurer.insurer_id);
    fetchPremium.mutate(insurer.insurer_id, {
      onSettled: () => setFetchingInsurerId(null),
      onSuccess: (result) => {
        setCodeGap(null);
        const count = result.quotes.length;
        toast.success(
          count === 1
            ? `${result.insurer_name} priced this quote at ${formatCurrency(
                Number(result.quotes[0].total_premium)
              )}`
            : `${result.insurer_name} returned ${count} option(s)`
        );
      },
      // The insurer's own words. "Pricing failed" tells a broker nothing they
      // can act on; "No GoDigit vehicle code for Maruti Swift VXI" names the
      // master-data row an admin has to import.
      onError: (err) => {
        const gap = vehicleCodeMissingFrom(err);
        if (gap) {
          setChosenVariantId('');
          setCodeGap({ insurer, gap });
          return;
        }
        setCodeGap(null);
        toast.error(getErrorMessage(err));
      },
    });
  }

  function applyChosenVehicle() {
    if (!codeGap || !chosenVariantId) return;
    const { insurer } = codeGap;
    setVehicleVariant.mutate(
      { variantId: chosenVariantId, insurerId: insurer.insurer_id },
      {
        onSuccess: () => {
          setCodeGap(null);
          fetchFrom(insurer);
        },
        // The picker stays open: the broker can choose another vehicle.
        onError: (err) => toast.error(getErrorMessage(err)),
      }
    );
  }

  // The add form and the edit form are mutually exclusive on purpose: two open
  // "Amount" fields on one card is an invitation to type the new figure into
  // the old option.
  const [addDraft, setAddDraft] = useState<PremiumOptionDraft | null>(null);
  const [showAddErrors, setShowAddErrors] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<OptionEditForm>({
    total_premium: '',
    valid_until: '',
    is_recommended: false,
  });

  /**
   * THE LINK THIS RETURNS IS THE POINT, AND IT USED TO BE THROWN AWAY.
   *
   * `share` (backend/apps/quotes/views.py) does exactly two things: it flips
   * `is_shared` -- which is what makes the customer's URL resolve at all -- and
   * it RETURNS that URL. It sends nothing. `share_via` is the endpoint that
   * delivers, and it is the one behind the two channel buttons below.
   *
   * This card used to call `share`, discard `data`, and toast "Quote sent — the
   * customer can now open their link". Nothing had been sent, and the broker
   * was left holding neither the link nor any way to deliver it. So the URL is
   * kept here, put on screen, and copied; the wording says publishing, not
   * sending; and sending is a separate, real press.
   */
  const [publicUrl, setPublicUrl] = useState<string | null>(null);

  const publishLink = useMutation({
    mutationFn: () => quoteService.shareQuote(requestId),
    onSuccess: (data) => {
      // `shareQuote` answers with a PATH; `shareQuoteVia` answers with an
      // absolute URL built from SITE_URL. Only the first needs an origin.
      const url = `${window.location.origin}${data.public_url}`;
      setPublicUrl(url);
      // `share` also moves the request onto the `quote_shared` rung
      // (best-effort), so the request has to be re-read or this card keeps
      // calling a published quote held.
      queryClient.invalidateQueries({ queryKey: ['quote', requestId] });
      toast.success('Public quote link ready');
      // Convenience only, and deliberately NOT announced: the clipboard is
      // unavailable on insecure origins and refusable by the browser, so a
      // toast claiming the copy would be another thing this card said without
      // it having happened. The link is on screen and can be copied by hand.
      navigator.clipboard?.writeText(url).catch(() => undefined);
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const sendVia = useMutation({
    mutationFn: (channel: 'whatsapp' | 'email') => quoteService.shareQuoteVia(requestId, channel),
    onSuccess: (data) => {
      // Already absolute here, unlike `share`'s answer.
      setPublicUrl(data.public_url);
      queryClient.invalidateQueries({ queryKey: ['quote', requestId] });
      toast.success(
        `Quote link sent to ${data.recipient} via ${data.channel === 'whatsapp' ? 'WhatsApp' : 'email'}`
      );
    },
    onError: (err) => {
      // `share_via` PUBLISHES the link and then tries to deliver it. Delivery
      // fails on this deployment (no channel provider is wired platform-wide),
      // but the 502 body still carries `public_url` -- the link really is live.
      // Dropping it would leave the broker with an error and no link: a
      // published resource thrown away, the same discarded-return-value defect
      // this card was just fixed for on its success path.
      const published = (err as { response?: { data?: { public_url?: string } } })?.response?.data
        ?.public_url;
      if (published) setPublicUrl(published);
      toast.error(getErrorMessage(err));
    },
  });

  const today = localDateKey(new Date());
  const rows = options ?? [];
  const isClosed = CLOSED_STATUSES.has(status);

  function openAdd(prefill?: Partial<PremiumOptionDraft>) {
    setEditingId(null);
    setShowAddErrors(false);
    setAddDraft({ ...emptyPremiumOption(), ...prefill });
  }

  function openEdit(option: QuoteOption) {
    setAddDraft(null);
    setEditingId(option.id);
    setEditForm({
      total_premium: String(option.total_premium ?? ''),
      valid_until: expiryDayFromTimestamp(option.valid_until),
      is_recommended: Boolean(option.is_recommended),
    });
  }

  function submitAdd() {
    if (!addDraft) return;
    if (Object.keys(premiumOptionErrors(addDraft, today)).length > 0) {
      setShowAddErrors(true);
      return;
    }
    addOption.mutate(premiumOptionToFormData(addDraft), {
      onSuccess: () => {
        setAddDraft(null);
        setShowAddErrors(false);
        toast.success('Premium option added');
      },
      onError: (err) => toast.error(getErrorMessage(err)),
    });
  }

  function saveEdit(option: QuoteOption) {
    // What may be sent is decided in ./proposalFlow, from `pricing_source` --
    // not here from which inputs this card drew. See buildOptionPatch.
    const patch = buildOptionPatch(option, editForm);

    if (Object.keys(patch).length === 0) {
      setEditingId(null);
      return;
    }

    updateOption.mutate(
      { optionId: option.id, patch },
      {
        onSuccess: () => {
          setEditingId(null);
          toast.success('Option updated');
        },
        onError: (err) => toast.error(getErrorMessage(err)),
      }
    );
  }

  return (
    <Card>
      <CardHeader
        title="Premium Options"
        subtitle="Every option on this quote, and where each price came from. The customer sees these on their quote link."
      />

      <HoldBanner
        isShared={isShared}
        sharedAt={sharedAt}
        isClosed={isClosed}
        status={status}
        canShare={canShare}
      />

      {canShare && !isClosed && (
        <SharePanel
          publicUrl={publicUrl}
          isPublishing={publishLink.isPending}
          onPublish={() => publishLink.mutate()}
          sendingChannel={sendVia.isPending ? sendVia.variables ?? null : null}
          onSend={(channel) => sendVia.mutate(channel)}
          proposerPhone={proposerPhone}
          proposerEmail={proposerEmail}
        />
      )}

      {isLoading ? (
        <ContentLoader />
      ) : isError ? (
        <p className="text-sm text-red-600 dark:text-red-400">
          The options on this quote could not be loaded: {getErrorMessage(error)}
        </p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          No premium options yet. Add the figures you have from each insurance company — the
          customer chooses between them on their quote link.
        </p>
      ) : (
        <ul className="space-y-3">
          {rows.map((option) => (
            <li key={option.id} data-testid={`quote-option-${option.id}`}>
              <OptionRow
                option={option}
                canEdit={!isClosed}
                isEditing={editingId === option.id}
                form={editForm}
                onForm={(patch) => setEditForm((f) => ({ ...f, ...patch }))}
                today={today}
                isSaving={updateOption.isPending}
                onEdit={() => openEdit(option)}
                onCancel={() => setEditingId(null)}
                onSave={() => saveEdit(option)}
                onCopyToManual={() =>
                  openAdd({
                    insurer: option.insurer,
                    insurer_name: option.insurer_name,
                    product: option.product ?? '',
                    product_name: option.product_name ?? '',
                    total_premium: String(option.total_premium ?? ''),
                    valid_until: expiryDayFromTimestamp(option.valid_until),
                  })
                }
              />
            </li>
          ))}
        </ul>
      )}

      {!isClosed && (integrated?.insurers?.length ?? 0) > 0 && (
        <div className="mt-5 rounded-lg border border-blue-100 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 p-4">
          <p className="text-sm font-medium text-blue-900 dark:text-blue-200">Fetch a live premium</p>
          <p className="mt-1 text-sm text-blue-800 dark:text-blue-300">
            These insurance companies are integrated for this broker, so their own price can be
            fetched instead of typed. The figure that comes back is theirs, and they hold an
            application at it.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {integrated!.insurers.map((insurer) => (
              <Button
                key={insurer.insurer_id}
                variant="outline"
                size="sm"
                isLoading={fetchPremium.isPending && fetchingInsurerId === insurer.insurer_id}
                disabled={fetchPremium.isPending}
                onClick={() => fetchFrom(insurer)}
              >
                Fetch premium from {insurer.name}
              </Button>
            ))}
          </div>

          {codeGap && (
            <div
              role="region"
              aria-label="Choose the vehicle"
              className="mt-4 rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3"
            >
              <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
                {codeGap.insurer.name} needs its own code for this vehicle
              </p>
              {codeGap.gap.detail && (
                <p className="mt-1 text-sm text-amber-800 dark:text-amber-300">{codeGap.gap.detail}</p>
              )}
              {codeGap.gap.candidates.length === 0 ? (
                <p className="mt-2 text-sm text-amber-900 dark:text-amber-200">
                  No vehicle in the {codeGap.insurer.name} catalogue matches this one. Ask your
                  country admin to map it (Vehicle Variants, Missing insurer code), or add the
                  premium by hand below.
                </p>
              ) : codeGap.gap.changeBlocked ? (
                <p className="mt-2 text-sm text-amber-900 dark:text-amber-200">
                  {codeGap.gap.changeBlocked} The {codeGap.insurer.name} catalogue has{' '}
                  {codeGap.gap.candidates.length === 1
                    ? '1 matching vehicle'
                    : `${codeGap.gap.candidates.length} matching vehicles`}
                  : {codeGap.gap.candidates.map((c) => c.label).join('; ')}.
                </p>
              ) : !canShare ? (
                <p className="mt-2 text-sm text-amber-900 dark:text-amber-200">
                  The vehicle on this quote has to be changed to one {codeGap.insurer.name}{' '}
                  recognises. Ask the broker&apos;s office to choose it.
                </p>
              ) : (
                <fieldset className="mt-3">
                  <legend className="text-sm text-amber-900 dark:text-amber-200">
                    Choose the exact vehicle. The quote will use it and the premium is fetched
                    again.
                  </legend>
                  <ul className="mt-2 space-y-1">
                    {codeGap.gap.candidates.map((candidate) => (
                      <li key={candidate.variant_id}>
                        <label className="flex items-center gap-2 text-sm text-gray-900 dark:text-white">
                          <input
                            type="radio"
                            name="vehicle-code-candidate"
                            value={candidate.variant_id}
                            checked={chosenVariantId === candidate.variant_id}
                            onChange={() => setChosenVariantId(candidate.variant_id)}
                          />
                          <span>{candidate.label}</span>
                          {candidate.insurer_code && (
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              code {candidate.insurer_code}
                            </span>
                          )}
                        </label>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      disabled={!chosenVariantId || fetchPremium.isPending}
                      isLoading={setVehicleVariant.isPending}
                      onClick={applyChosenVehicle}
                    >
                      Use this vehicle and fetch again
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setCodeGap(null)}>
                      Cancel
                    </Button>
                  </div>
                </fieldset>
              )}
            </div>
          )}
        </div>
      )}

      {!isClosed && (
        <div className="mt-5 border-t border-gray-100 dark:border-gray-700 pt-5">
          {addDraft ? (
            <div className="space-y-4">
              <PremiumOptionRow
                index={0}
                title="New premium option"
                idPrefix="add-option"
                option={addDraft}
                insuranceTypeId={insuranceTypeId}
                vehicleCategory={vehicleCategory}
                insurerOptions={insurerOptions}
                insurersLoading={insurersLoading}
                canRemove={false}
                errors={showAddErrors ? premiumOptionErrors(addDraft, today) : {}}
                onChange={(patch) => setAddDraft((d) => (d ? { ...d, ...patch } : d))}
                onRemove={() => setAddDraft(null)}
              />
              <div className="flex items-center gap-3">
                <Button onClick={submitAdd} isLoading={addOption.isPending}>
                  Add option
                </Button>
                <Button variant="ghost" onClick={() => setAddDraft(null)}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <Button
              variant="outline"
              leftIcon={<PlusIcon className="w-5 h-5" />}
              onClick={() => openAdd()}
            >
              Add a premium option
            </Button>
          )}
        </div>
      )}
    </Card>
  );
}

/**
 * Held or sent, said plainly.
 *
 * Keyed on `is_shared` rather than on the status string because `is_shared` is
 * what the public endpoint checks: until a broker publishes, the customer's URL
 * 404s no matter what rung the request is on. Sharing then moves the status to
 * `quote_shared` as a side effect (`QuoteRequestViewSet._publish`), never the
 * reverse.
 */
function HoldBanner({
  isShared,
  sharedAt,
  isClosed,
  status,
  canShare,
}: {
  isShared: boolean;
  sharedAt: string | null;
  isClosed: boolean;
  status: string;
  /** Only a viewer who HAS the send buttons may be pointed at them. */
  canShare: boolean;
}) {
  if (isClosed) {
    return (
      <div className="mb-5 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 p-4">
        <Badge size="sm">{titleCase(status.replace(/_/g, ' '))}</Badge>
        <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
          This quote is closed and no longer waiting to go out. Its options are shown as they stand.
        </p>
      </div>
    );
  }

  if (isShared) {
    // "Sent" was the wrong word and is the same lie D2 was about: `is_shared`
    // is flipped by `share`, which publishes the link and delivers NOTHING.
    // (`share_via` sets it too, but only as a side effect of really sending, so
    // the flag cannot tell the two apart.) It says the link resolves. It does
    // not say anybody received it.
    return (
      <div className="mb-5 rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 p-4">
        <div className="flex items-center gap-2">
          <Badge variant="success" size="sm">
            Link live
          </Badge>
        </div>
        <p className="mt-2 text-sm text-green-800 dark:text-green-300">
          The customer&apos;s quote link is open
          {sharedAt ? ` (published ${formatDate(sharedAt)})` : ''}, so anything you change below
          shows up there straight away.
          {canShare
            ? ' Publishing is not sending — use Share via WhatsApp or Email below to put the link in front of them.'
            : ''}
        </p>
      </div>
    );
  }

  return (
    <div className="mb-5 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4">
      <Badge variant="warning" size="sm">
        Held
      </Badge>
      <p className="mt-2 text-sm text-amber-800 dark:text-amber-300">
        Not sent yet — the customer&apos;s quote link does not open, so these options are still yours
        to add to and adjust.
      </p>
    </div>
  );
}

/**
 * PUBLISH, THEN SEND. Two acts, because the server has two endpoints and they
 * do different things.
 *
 * `share` makes the customer's URL resolve and hands the URL back; it delivers
 * nothing. `share_via` delivers, through the broker's own configured channel,
 * provider and template -- and publishes on the way, so either order works.
 *
 * This is the ONLY share control on the quote screen. QuoteDetailPage used to
 * carry a second copy in its "Quote Status" card, invisible to POSPs and
 * unreachable from the button that claimed to send; it was removed in favour of
 * this one, which sits beside the options the customer is being sent.
 */
function SharePanel({
  publicUrl,
  isPublishing,
  onPublish,
  sendingChannel,
  onSend,
  proposerPhone,
  proposerEmail,
}: {
  publicUrl: string | null;
  isPublishing: boolean;
  onPublish: () => void;
  sendingChannel: 'whatsapp' | 'email' | null;
  onSend: (channel: 'whatsapp' | 'email') => void;
  proposerPhone?: string | null;
  proposerEmail?: string | null;
}) {
  const isSending = sendingChannel !== null;

  return (
    <div className="mb-5 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
      {/* One label in both states, because the endpoint is idempotent: pressing
          it on an already-published quote returns the SAME token, which is how
          a broker who has reloaded the page gets the link back. */}
      <Button
        variant="outline"
        leftIcon={<LinkIcon className="w-5 h-5" />}
        isLoading={isPublishing}
        onClick={onPublish}
      >
        Create the customer&rsquo;s link
      </Button>
      <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
        That opens the customer&apos;s quote link and hands it back &mdash; it does not send
        anything. The two buttons below do send, through your own configured channel and template,
        and open the link on the way if it is not open yet.
      </p>

      {publicUrl && (
        <p className="mt-3 break-all text-xs text-gray-600 dark:text-gray-300">
          Shareable link: <span className="font-mono">{publicUrl}</span>
        </p>
      )}

      {/* NOT gated on having published first: `share_via` publishes as it
          sends, so demanding a press of the button above would be a step the
          server does not need.

          Each is disabled when the quote carries no address to send to -- the
          server refuses that case, and a button that always fails is worse than
          one that explains itself. */}
      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
        <Button
          variant="outline"
          size="sm"
          leftIcon={<PaperAirplaneIcon className="w-4 h-4" />}
          isLoading={sendingChannel === 'whatsapp'}
          disabled={isSending || !proposerPhone}
          title={
            proposerPhone ? `Send to ${proposerPhone}` : 'This quote has no proposer mobile number'
          }
          onClick={() => onSend('whatsapp')}
        >
          Share via WhatsApp
        </Button>
        <Button
          variant="outline"
          size="sm"
          leftIcon={<PaperAirplaneIcon className="w-4 h-4" />}
          isLoading={sendingChannel === 'email'}
          disabled={isSending || !proposerEmail}
          title={
            proposerEmail ? `Send to ${proposerEmail}` : 'This quote has no proposer email address'
          }
          onClick={() => onSend('email')}
        >
          Share via Email
        </Button>
      </div>
    </div>
  );
}

interface OptionRowProps {
  option: QuoteOption;
  canEdit: boolean;
  isEditing: boolean;
  form: OptionEditForm;
  onForm: (patch: Partial<OptionEditForm>) => void;
  today: string;
  isSaving: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onSave: () => void;
  onCopyToManual: () => void;
}

function OptionRow({
  option,
  canEdit,
  isEditing,
  form,
  onForm,
  today,
  isSaving,
  onEdit,
  onCancel,
  onSave,
  onCopyToManual,
}: OptionRowProps) {
  // Trusts the server's `pricing_source`, which is derived from the one field
  // the payment path actually reads. Re-deriving it here would be a second
  // source of truth that could disagree with the endpoint that refuses.
  const isApiPriced = option.pricing_source === 'insurer_api';
  const policy = optionEditPolicy(option.pricing_source);
  const errors = premiumFigureErrors(form.total_premium, form.valid_until, today);

  return (
    <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
            {option.insurer_name}
            {/* One insurer can price the same vehicle up to three ways on one
                request; the cover is what tells those rows apart. */}
            {coverLabel(option) && (
              <span className="ml-2" data-testid="option-cover-label">
                <Badge variant="default" size="sm">
                  {coverLabel(option)}
                </Badge>
              </span>
            )}
          </p>
          <p className="truncate text-xs text-gray-500 dark:text-gray-400">{option.product_name || '\u2014'}</p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {/* Visible WITHOUT opening the editor -- the flag used to be legible
              only as a checkbox state inside the form that set it. */}
          {option.is_recommended && (
            <Badge variant="success" size="sm">
              Recommended
            </Badge>
          )}
          {/* #399: written by the insurer's own underwriter on the Quote Desk. */}
          {option.insurer_approved ? (
            <Badge variant="success" size="sm">
              Insurer approved
            </Badge>
          ) : (
            <Badge variant={isApiPriced ? 'info' : 'default'} size="sm">
              {isApiPriced ? 'Insurer API' : 'Manual'}
            </Badge>
          )}
          {canEdit && !isEditing && (
            <Button variant="outline" size="sm" onClick={onEdit}>
              Edit
            </Button>
          )}
        </div>
      </div>

      <dl className="mt-3 grid grid-cols-1 gap-3 text-sm sm:grid-cols-3">
        <Figure label="Premium" value={formatCurrency(option.total_premium)} />
        {/* NOT `formatDate`. Every expiry here is stored as `...T23:59:59` UTC,
            so reading the stamp through the browser's calendar shows the NEXT
            day east of Greenwich -- while the editor two fields down prefilled
            the UTC day. The figure and the field disagreed, and a broker
            "correcting" the field walked the date forward on every save. One
            reading, and `formatExpiryDay` is it. */}
        <Figure label="Valid until" value={formatExpiryDay(option.valid_until)} />
        <Figure
          label="Priced by"
          value={
            isApiPriced
              ? `Insurer API \u00b7 ref ${option.insurer_quote_id}`
              : 'Typed in by your team'
          }
        />
      </dl>

      {(option.documents?.length ?? 0) > 0 && (
        <ul className="mt-3 space-y-1">
          {option.documents?.map((doc) => (
            <li key={doc.id} className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
              <PaperClipIcon className="w-4 h-4 shrink-0 text-gray-400" />
              <span className="truncate">{doc.name}</span>
            </li>
          ))}
        </ul>
      )}

      <LifeIllustrationTable illustration={option.illustration} />

      {isEditing && (
        <div className="mt-4 border-t border-gray-100 dark:border-gray-700 pt-4">
          {policy.canEditFigures ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input
                id="edit-option-amount"
                label="Amount"
                type="number"
                inputMode="decimal"
                min="0"
                step="0.01"
                value={form.total_premium}
                onChange={(e) => onForm({ total_premium: e.target.value })}
                error={errors.total_premium}
              />
              <Input
                id="edit-option-expiry"
                label="Expiry Date"
                type="date"
                value={form.valid_until}
                onChange={(e) => onForm({ valid_until: e.target.value })}
                error={errors.valid_until}
              />
            </div>
          ) : (
            <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 p-3">
              {/* Written by `optionEditPolicy` and rendered verbatim -- the same
                  discipline the issuance-readiness blockers follow. Rewording it
                  here is how the screen starts saying something the flow does
                  not do. */}
              <p className="text-sm text-blue-900 dark:text-blue-200">{policy.lockedReason}</p>
              <Button variant="outline" size="sm" className="mt-3" onClick={onCopyToManual}>
                Copy as a manual option
              </Button>
            </div>
          )}

          {/* Editable on BOTH kinds, because no insurer, payment or issuance
              path reads it -- see optionEditPolicy.

              THE HELPER TEXT IS THE FIX, not decoration. This control used to be
              labelled "Highlight this option to the customer" and did nothing at
              all: `is_recommended` was stored and had no reader anywhere in the
              frontend. It now orders and badges the compare screen and badges
              the row above. It still does NOT mark anything on the customer's
              own quote link (pages/public/PublicQuote.tsx), so the label must
              not say it does until that page reads it. */}
          <label
            htmlFor={`recommend-${option.id}`}
            className="mt-4 flex items-start gap-2 text-sm text-gray-700 dark:text-gray-200"
          >
            <input
              id={`recommend-${option.id}`}
              type="checkbox"
              checked={form.is_recommended}
              onChange={(e) => onForm({ is_recommended: e.target.checked })}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-primary-600 dark:text-primary-400 focus:ring-primary-500"
            />
            <span>
              Recommend this option
              <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">
                It is badged here and leads the compare screen, above the premium sort. The
                customer&apos;s own quote link does not mark it yet.
              </span>
            </span>
          </label>

          <div className="mt-4 flex items-center gap-3">
            <Button
              size="sm"
              isLoading={isSaving}
              disabled={policy.canEditFigures && Object.keys(errors).length > 0}
              onClick={onSave}
            >
              Save changes
            </Button>
            <Button variant="ghost" size="sm" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-gray-500">{label}</dt>
      <dd className="mt-0.5 font-medium text-gray-900 dark:text-gray-100">{value}</dd>
    </div>
  );
}

export default QuotePremiumOptionsCard;
